import { Route } from "./+types/$slug";
import { SERVICE_AREA_QUERY, ServiceAreaQuery } from "#app/queries/service-area.ts";
import { Heading, PageHeader, Text } from "#app/components/text.tsx";
import { DocumentCard } from "#app/components/documen-list-section.tsx";
import { useState } from "react";
import Cta from "#app/components/cta.tsx";
import { Link } from "react-router";
import { buttonVariants } from "#app/components/ui/button.tsx";
import { Icon } from "#app/components/ui/icon.tsx";
import { PortableText } from "@portabletext/react";
import { richTextComponents } from "#app/components/rich-text.tsx";
import { BASE_URL, COMPANY_NAME } from "#app/utils/constants.ts";
import { getSeoMeta } from "#app/utils/seo/getSeoMeta.ts";
import { breadcrumbSchema, serviceSchema } from "#app/utils/seo/structured-data.ts";


export const meta: Route.MetaFunction = ({ data }) => {
    const serviceArea = data?.serviceAreaData.serviceArea;
    const title = serviceArea?.seo?.metaTitle || COMPANY_NAME;
    const description = serviceArea?.seo?.metaDescription || `${COMPANY_NAME} residential services`;
    const path = '/service-areas/' + serviceArea?.slug?.current;
    const url = BASE_URL + path;
    const cityName = serviceArea?.name;
    return getSeoMeta({
        title,
        description,
        url,
        jsonLd: [
            // Scoped to this city, so the city page says something the
            // site-wide business node does not.
            serviceSchema({
                name: `Garage Door Repair and Installation in ${cityName ?? 'Central Florida'}`,
                description: serviceArea?.seo?.metaDescription,
                url,
                areaServed: cityName ? [cityName] : undefined,
                areaType: 'City',
            }),
            breadcrumbSchema([
                { name: 'Home', path: '/' },
                { name: 'Service Areas', path: '/service-areas' },
                { name: cityName || 'Service Area', path },
            ]),
        ] as any,
    })	
}

export const loader = async ({ request, params }: Route.LoaderArgs) => {
    const { client } = await import('#app/utils/sanity-client.ts');
    const query = SERVICE_AREA_QUERY.query
    const serviceAreaData = await client.fetch<ServiceAreaQuery>(query, params)

    // serviceAreaData is the query wrapper and is always truthy, so checking it
    // let unknown slugs render a 200 page with "undefined" in the heading.
    // The area itself is what decides whether this page exists.
    if (!serviceAreaData?.serviceArea) {
        throw new Response('Not found', { status: 404 })
    }
    return {
        serviceAreaData
    }
}


export default function ServicePage({loaderData}: Route.ComponentProps) {
    const {serviceAreaData} = loaderData
    const { serviceArea, residentialServices, commercialServices } = serviceAreaData;
    const [residentialHovered, setResidentialHovered] = useState<number | null>(null);
    const [commercialHovered, setCommercialHovered] = useState<number | null>(null);
    return (
        <main>
            <PageHeader variant="servicesPage" heading={`Garage Door Repair and Installation in ${serviceArea?.name}`} className="bg-primary text-primary-foreground text-center mb-24 sm:mb-32">
                <Text size="lg">
                    {serviceArea?.description ?? "No description available."}
                </Text>
            </PageHeader>
            {/* The only part of this page unique to the area — everything below
                is shared across all service-area pages. */}
            {serviceArea?.localContent?.length ? (
                <section className="container mb-24 sm:mb-32">
                    <div className="prose dark:prose-invert mx-auto max-w-prose">
                        <PortableText
                            value={serviceArea.localContent as any}
                            components={richTextComponents}
                        />
                    </div>
                </section>
            ) : null}
            <section className="bg-background mb-24 sm:mb-32">
                <div className="container px-6 lg:px-8">
                    <Heading className='mx-auto text-5xl text-center'>
                        Residential Services
                    </Heading>
                    {residentialServices && residentialServices.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 mt-16"> {/* Use grid layout */}
                            {residentialServices.map((service, index) => (
                                <DocumentCard
                                    // Key should be stable and unique, _id is best
                                    key={index}
                                    // Ensure the 'service' object matches the expected 'document' prop type
                                    // You might need to adjust zDocumentListItem if RESIDENTIAL_SERVICES_QUERY returns a slightly different shape
                                    document={service as any} // Cast or ensure type match
                                    index={index}
                                    hovered={residentialHovered}
                                    setHovered={setResidentialHovered}
                                />
                            ))}
                        </div>
                    ) : (
                            <p className="text-center text-lg">No residential services found at this time. Please check back later.</p> // Message if no services
                    )}
                </div>
            </section>
            <section className="bg-background mb-24 sm:mb-32">
                <div className="container px-6 lg:px-8">
                    <Heading className='mx-auto text-5xl text-center'>
                        Commercial Services
                    </Heading>
                    {commercialServices && commercialServices.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 mt-16"> {/* Use grid layout */}
                            {commercialServices.map((service, index) => (
                                <DocumentCard
                                    // Key should be stable and unique, _id is best
                                    key={index}
                                    // Ensure the 'service' object matches the expected 'document' prop type
                                    // You might need to adjust zDocumentListItem if RESIDENTIAL_SERVICES_QUERY returns a slightly different shape
                                    document={service as any} // Cast or ensure type match
                                    index={index}
                                    hovered={commercialHovered}
                                    setHovered={setCommercialHovered}
                                />
                            ))}
                        </div>
                    ) : (
                            <p className="text-center text-lg">No commercial services found at this time. Please check back later.</p> // Message if no services
                    )}
                </div>
            </section>
            <Cta heading="Need a Repair or Installation Quote?" backgroundStyle="accent">
                <Link to="/get-a-quote" className={buttonVariants({ size: "xl" })}>
                    Get a free quote
                    <Icon name="dollar-sign" />
                </Link>
                <Link to="tel:5133841424" className={buttonVariants({ variant: "white", size: "xl" })}>
                    (513) 384-1424
                    <Icon name="phone-outgoing" />
                </Link> 
            </Cta>
        </main>
    )
}